import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { saveAs } from "file-saver";
import AlertDialog from "./AlertDialog";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

export default function WorkOrderViewNew() {
  const { id, woId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [users, setUsers] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [progressEditing, setProgressEditing] = useState({});
  const [pendingItems, setPendingItems] = useState([]);
  // Add this state variable near the top of the component
  const [timesheetSummary, setTimesheetSummary] = useState(null);
  const [highlightedItems, setHighlightedItems] = useState(new Set());
  const [editTitle, setEditTitle] = useState("");
  const [editType, setEditType] = useState("");
  const [editUnion, setEditUnion] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [pieceworkerSummary, setPieceworkerSummary] = useState(null);

  useEffect(() => {
    const fetchTimesheetSummary = async () => {
      if (workOrder?.type === "hourly") {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/workorders/${woId}/timesheets`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const summary = calculateTimesheetSummary(response.data);
          setTimesheetSummary(summary);
        } catch (err) {
          console.error("Failed to fetch timesheet data:", err);
          setTimesheetSummary(null);
        }
      } else {
        setTimesheetSummary(null);
      }
    };

    fetchTimesheetSummary();
  }, [workOrder?.type, woId]);

  // Add this useEffect to fetch pieceworker log summary
  useEffect(() => {
    const fetchPieceworkerSummary = async () => {
      if (workOrder?.type === "piecework") {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs/${woId}/pieceworker-logs`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          console.log(response.data);

          const summary = calculatePieceworkerSummary(response.data);
          setPieceworkerSummary(summary);
        } catch (err) {
          console.error("Failed to fetch pieceworker log data:", err);
          setPieceworkerSummary(null);
        }
      } else {
        setPieceworkerSummary(null);
      }
    };

    fetchPieceworkerSummary();
  }, [workOrder?.type, woId]);

  // Add this helper function to calculate pieceworker summary
  const calculatePieceworkerSummary = (pieceworkerLogs) => {
    if (
      !pieceworkerLogs ||
      !Array.isArray(pieceworkerLogs) ||
      pieceworkerLogs.length === 0
    ) {
      return null;
    }

    const totalQuantity = pieceworkerLogs.reduce((sum, log) => {
      return (
        sum +
        log.lineItems.reduce(
          (lineSum, item) => lineSum + (item.qtyInstalled || 0),
          0,
        )
      );
    }, 0);

    const totalCost = pieceworkerLogs.reduce((sum, log) => {
      return (
        sum +
        (log.totalCost ||
          log.lineItems.reduce(
            (lineSum, item) => lineSum + (item.total || 0),
            0,
          ))
      );
    }, 0);

    const totalSubtotal = pieceworkerLogs.reduce((sum, log) => {
      return (
        sum +
        log.lineItems.reduce(
          (lineSum, item) => lineSum + (item.subtotal || 0),
          0,
        )
      );
    }, 0);

    const totalBurdenCost = pieceworkerLogs.reduce((sum, log) => {
      return (
        sum +
        log.lineItems.reduce(
          (lineSum, item) => lineSum + (item.pieceworkerBurdenCost || 0),
          0,
        )
      );
    }, 0);

    const totalWsibCost = pieceworkerLogs.reduce((sum, log) => {
      return (
        sum +
        log.lineItems.reduce(
          (lineSum, item) => lineSum + (item.wsibCost || 0),
          0,
        )
      );
    }, 0);

    return {
      totalQuantity,
      totalCost,
      totalSubtotal,
      totalBurdenCost,
      totalWsibCost,
      logCount: pieceworkerLogs.length,
    };
  };

  // Add this helper function to calculate timesheet summary
  const calculateTimesheetSummary = (timesheets) => {
    if (!timesheets || !Array.isArray(timesheets) || timesheets.length === 0) {
      return null;
    }

    const totalHours = timesheets.reduce((sum, timesheet) => {
      if (!timesheet.start || !timesheet.end) return sum;

      const start = new Date(timesheet.start);
      const end = new Date(timesheet.end);
      let durationMs = end.getTime() - start.getTime();

      // Subtract break durations
      if (timesheet.breaks && Array.isArray(timesheet.breaks)) {
        const totalBreakMinutes = timesheet.breaks.reduce(
          (breakSum, brk) => breakSum + (brk.duration || 0),
          0,
        );
        durationMs -= totalBreakMinutes * 60 * 1000;
      }

      return sum + durationMs / (1000 * 60 * 60); // Convert to hours
    }, 0);

    const totalCost = timesheets.reduce(
      (sum, timesheet) => sum + (timesheet.totalCost || 0),
      0,
    );
    const totalSubtotal = timesheets.reduce(
      (sum, timesheet) => sum + (timesheet.subtotal || 0),
      0,
    );
    const totalBurdenCost = timesheets.reduce(
      (sum, timesheet) => sum + (timesheet.burdenCost || 0),
      0,
    );
    const totalWsibCost = timesheets.reduce(
      (sum, timesheet) => sum + (timesheet.wsibCost || 0),
      0,
    );

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      totalCost,
      totalSubtotal,
      totalBurdenCost,
      totalWsibCost,
      timesheetCount: timesheets.length,
    };
  };

  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [
          projectResponse,
          usersResponse,
          costCodesResponse,
          companyProfileResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/users/subcontractors`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/costCodes`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);

        const projectData = projectResponse.data;
        setProject(projectData);
        setUsers(usersResponse.data);
        setCostCodes(costCodesResponse.data);
        setCompanyProfile(companyProfileResponse.data);

        const woData = projectData.workOrders.find((w) => w._id === woId);
        if (!woData) throw new Error("Work Order not found");
        setWorkOrder(woData);
        setEditTitle(woData.title);
        setEditType(woData.type);
        setEditUnion(woData.union || "non-union");
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    };
    fetchData();
  }, [id, woId]);

  const isTypeEditable = () => {
    return !workOrder?.lineItems.some(
      (item) => (item.qtyInstalled || 0) > 0 || (item.qtyPending || 0) > 0,
    );
  };

  const isUnionEditable = () => {
    return !workOrder?.lineItems.some(
      (item) =>
        (item.qtyInstalled || 0) > 0 ||
        (item.qtyPending || 0) > 0 ||
        (item.progressUpdates && item.progressUpdates.length > 0),
    );
  };

  const handleTitleChange = (e) => {
    setEditTitle(e.target.value);
    setWorkOrder((prev) => ({ ...prev, title: e.target.value }));
  };

  const handleSaveWorkOrder = async () => {
    try {
      const token = localStorage.getItem("token");

      const payload = {
        title: editTitle,
        type: editType,
        costCode: workOrder.costCode,
        estimatedCost: workOrder.estimatedCost,
        lineItems: workOrder.lineItems.map((item) => ({
          ...item,
          costCode: item.costCode || workOrder.costCode,
          budgetItem: item.budgetItem || null,
        })),
        termsAndConditions: workOrder.termsAndConditions,
        scopeOfWork: workOrder.scopeOfWork,
        assignedTo: workOrder.assignedTo,
        status: workOrder.status,
        union: editUnion,
      };

      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/workorders/${woId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update project state to reflect linkedWO changes
      const updatedProject = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProject.data);

      // Update workOrder state with the response data
      setWorkOrder(response.data);
      setEditing(false);
      setHighlightedItems(new Set());

      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Work Order updated successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update Work Order: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleTypeChange = (selectedOption) => {
    const newType = selectedOption ? selectedOption.value : workOrder.type;
    if (!isTypeEditable()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot change type when qtyInstalled or qtyPending exists.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const invalidItems = workOrder.lineItems.filter((item) =>
      newType === "hourly" ? item.uom !== "HR" : item.uom === "HR",
    );
    if (invalidItems.length > 0) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: `Cannot change to ${newType} type: Some items have incompatible UOM.`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setEditType(newType);
    setWorkOrder((prev) => ({
      ...prev,
      type: newType,
      union: newType === "subcontractor" ? "non-union" : prev.union,
    }));
    if (newType === "subcontractor") {
      setEditUnion("non-union");
    }
  };

  const handleUnionChange = (selectedOption) => {
    const newUnion = selectedOption ? selectedOption.value : "non-union";
    if (!isUnionEditable()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Cannot change union when there are installed quantities, pending quantities, or progress updates.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setEditUnion(newUnion);
    setWorkOrder((prev) => ({ ...prev, union: newUnion }));
  };

  const handleItemSelect = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    const newPendingItems = availableItems
      .filter((item) => selectedIds.includes(item._id))
      .map((item) => ({
        budgetItem: item._id,
        title: item.title || item.costCodeDescription || "",
        costCode: workOrder.costCode,
        bidArea: item.bidArea,
        qtyEstimated: item.quantity || 0,
        unitCost: item.unitCost || item.amount / (item.quantity || 1) || 0,
        uom: item.uom || "",
        laborHoursEstimated: item.laborHours || 0,
        progressUpdates: [],
      }));

    setPendingItems(newPendingItems);
  };

  const confirmAddItems = () => {
    setWorkOrder((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        ...pendingItems.filter(
          (newItem) =>
            !prev.lineItems.some((li) => li.budgetItem === newItem.budgetItem),
        ),
      ],
    }));

    const newItemIds = new Set(pendingItems.map((item) => item.budgetItem));
    setHighlightedItems((prev) => new Set([...prev, ...newItemIds]));
    setPendingItems([]);
    setShowAddDropdown(false);

    setTimeout(() => {
      setHighlightedItems((prev) => {
        const updated = new Set(prev);
        newItemIds.forEach((id) => updated.delete(id));
        return updated;
      });
    }, 3000);
  };

  // Remaining functions (handleProgressChange, handleProgressUpdate, etc.) remain unchanged
  const handleProgressChange = (index, value) => {
    setProgressEditing((prev) => ({
      ...prev,
      [index]: parseFloat(value) || 0,
    }));
  };

  const handleProgressUpdate = async (index, lineItemId) => {
    try {
      const token = localStorage.getItem("token");
      const percentCompleted = progressEditing[index];
      if (percentCompleted < 0 || percentCompleted > 100) {
        throw new Error("Percentage must be between 0 and 100");
      }

      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/workorders/${woId}/progress`,
        { lineItemId, percentCompleted },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setWorkOrder(response.data);
      setProgressEditing((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });

      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Progress updated successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update progress: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setShowAddDropdown(false);
    setPendingItems([]);
    setHighlightedItems(new Set());
    setEditTitle(workOrder.title);
    setEditType(workOrder.type);
    setEditUnion(workOrder.union || "non-union");
    navigate(`/Projects/${id}`, { state: { initialTab: "Work Orders" } });
  };

  const toggleHistory = (index) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleScopeChange = (e) => {
    setWorkOrder((prev) => ({ ...prev, scopeOfWork: e.target.value }));
  };

  const handleTermsChange = (e) => {
    setWorkOrder((prev) => ({ ...prev, termsAndConditions: e.target.value }));
  };

  const handleCostCodeChange = (selectedOption) => {
    setWorkOrder((prev) => ({
      ...prev,
      costCode: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleAssigneeChange = (selectedOption) => {
    setWorkOrder((prev) => ({
      ...prev,
      assignedTo: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleRemoveItem = (index, item) => {
    if (!canEditOrRemoveItem(item)) {
      setDialog({
        open: true,
        type: "error",
        title: "Cannot Remove Item",
        message:
          "This item cannot be removed because it has pending charges (installed quantities, pending quantities, or progress updates).",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Removal",
      message: `Are you sure you want to remove "${item.title}" from the work order?`,
      onConfirm: () => {
        setWorkOrder((prev) => ({
          ...prev,
          lineItems: prev.lineItems.filter((_, i) => i !== index),
        }));
        setDialog((prev) => ({ ...prev, open: false }));
      },
      onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleDownloadWO = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/workorders/${woId}/getCompany`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const assignedUser = response.data;

      const templateResponse = await fetch("/work_order_template.docx");
      if (!templateResponse.ok) {
        throw new Error(
          `Failed to load template: ${templateResponse.statusText}`,
        );
      }
      const templateArrayBuffer = await templateResponse.arrayBuffer();

      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) {
          return null;
        }

        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error(
            "Error parsing base64 data, your data contains invalid characters",
          );
        }

        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }

      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const reader = new FileReader();
          imageBase64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        } else {
          console.warn("Failed to fetch logo, skipping image");
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });

      const data = {
        companyName: companyProfile?.companyName || "N/A",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        companyPhone: companyProfile?.phone || "N/A",
        subcontractorName: assignedUser?.displayName || "N/A",
        subcontractorCompany: assignedUser?.account?.companyName || "N/A",
        subcontractorAddress: assignedUser?.account?.address
          ? `${assignedUser.account.address.street}, ${assignedUser.account.address.city}, ${assignedUser.account.address.state} ${assignedUser.account.address.postalCode}, ${assignedUser.account.address.country}`
          : "N/A",
        projectName: project?.projectName || "N/A",
        projectAddress: project?.address
          ? `${project.address.street}, ${project.address.city}, ${project.address.state} ${project.address.postalCode}, ${project.address.country}`
          : project?.location || "N/A",
        woNumber: workOrder?.woNumber || "N/A",
        title: workOrder.title,
        woDate: new Date().toLocaleDateString(),
        scopeOfWork: workOrder?.scopeOfWork || "N/A",
        termsAndConditions: workOrder?.termsAndConditions || "N/A",
        union: workOrder?.union || "Non-Union",
        lineItems:
          workOrder?.lineItems.map((item) => ({
            title: item.title || "N/A",
            qtyEstimated: item.qtyEstimated || 0,
            unitCost: item.unitCost ? item.unitCost.toFixed(2) : "0.00",
            totalCost: (item.qtyEstimated * item.unitCost).toFixed(2),
            costCode: item.costCode.code,
            uom: item.uom || "N/A",
            laborHours: item.laborHoursEstimated || 0,
            bidArea: item.bidArea || "N/A",
          })) || [],
        totalCost: workOrder?.lineItems
          .reduce((sum, item) => sum + item.qtyEstimated * item.unitCost, 0)
          .toFixed(2),
        image: imageBase64,
      };

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append("docx", docxBlob, `WorkOrder_${workOrder.woNumber}.docx`);
      formData.append("documentType", "WorkOrder");
      formData.append("identifier", workOrder.woNumber);

      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        saveAs(pdfBlob, `WorkOrder_${workOrder.woNumber}.pdf`);
        setDialog({
          open: true,
          type: "success",
          title: "Success",
          message: "Work Order PDF generated successfully!",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      } else {
        const textResponse = await pdfResponse.data.text();
        let errorData;
        try {
          errorData = JSON.parse(textResponse);
        } catch {
          errorData = { message: textResponse };
        }
        throw new Error(errorData.message || "Unknown error from server");
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to generate or convert Work Order: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const canEditOrRemoveItem = (item) => {
    return (
      workOrder.status === "draft" &&
      !(item.qtyInstalled && item.qtyInstalled > 0) &&
      !(item.qtyPending && item.qtyPending > 0) &&
      (!item.progressUpdates || item.progressUpdates.length === 0)
    );
  };

  const availableItems =
    project?.budgets
      ?.flatMap((budget) =>
        budget.isVariance
          ? []
          : budget.lineItems
              .filter((item) => !item.linkedWO && item.category === "L")
              .map((item) => ({
                ...item,
                source: "budget",
                budgetTitle: budget.title,
              })),
      )
      ?.filter(
        (item) =>
          !project.workOrders.some((w) =>
            w.lineItems.some((li) => li.budgetItem === item._id),
          ) &&
          (workOrder.type === "hourly" || item.uom !== "HR"),
      ) || [];

  const availableItemOptions = availableItems.map((item) => ({
    value: item._id,
    label: `${item.budgetTitle}: ${
      item.title || item.costCodeDescription
    } (Qty: ${item.quantity || 1}, Cost: ${item.amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    })} ,Labor Hours: ${item.laborHours || 0}, UOM: ${item.uom || "N/A"})`,
  }));

  const subcontractorOptions = users.map((user) => ({
    value: user._id,
    label: `${user.displayName} (${user.payType})`,
  }));

  const costCodeOptions = costCodes.map((cc) => ({
    value: cc._id,
    label: `${cc.code} - ${cc.description}`,
  }));

  const unionOptions = [
    { value: "non-union", label: "Non-Union" },
    ...(companyProfile?.unions?.map((union) => ({
      value: union.title,
      label: union.title,
    })) || []),
  ];

  if (!project || !workOrder)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  return (
    <div className=" mx-auto px-2 py-6">
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-white">
            {workOrder.title} (WO# {workOrder.woNumber})
          </h1>
          <button
            onClick={() =>
              navigate(`/Projects/${id}`, {
                state: { initialTab: "Work Orders" },
              })
            }
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>
      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div></div>
          {!editing ? (
            <div className="flex space-x-4">
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Edit
              </button>
              {workOrder?.type === "subcontractor" && (
                <button
                  onClick={handleDownloadWO}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Download WO
                </button>
              )}
            </div>
          ) : (
            <div className="flex space-x-4">
              <button
                onClick={() => setShowAddDropdown(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Add Labor Items
              </button>
              <button
                onClick={handleSaveWorkOrder}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Save Work Order
              </button>
              <button
                onClick={cancelEditing}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400">
            Title
          </label>
          {editing ? (
            <input
              type="text"
              value={editTitle}
              onChange={handleTitleChange}
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          ) : (
            <p className="mt-1 text-sm text-gray-200">
              {workOrder.title || "N/A"}
            </p>
          )}
        </div>

        {/* Work Order Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400">
            Work Order Type
          </label>
          {editing ? (
            <Select
              options={[
                { value: "hourly", label: "Hourly" },
                { value: "piecework", label: "Piecework" },
                { value: "subcontractor", label: "Subcontractor" },
              ]}
              value={{
                value: editType,
                label: editType
                  ? editType.charAt(0).toUpperCase() + editType.slice(1)
                  : "N/A",
              }}
              onChange={handleTypeChange}
              placeholder="Select a type..."
              className="w-full mt-1"
              styles={customStyles}
              isDisabled={!isTypeEditable()}
            />
          ) : (
            <p className="mt-1 text-sm text-gray-200 capitalize">
              {workOrder.type || "N/A"}
            </p>
          )}
        </div>

        {/* Union - Only for Hourly or Piecework */}
        {(workOrder.type === "hourly" || workOrder.type === "piecework") && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400">
              Union
            </label>
            {editing ? (
              <Select
                options={unionOptions}
                value={
                  unionOptions.find((opt) => opt.value === editUnion) || {
                    value: "non-union",
                    label: "Non-Union",
                  }
                }
                onChange={handleUnionChange}
                placeholder="Select a union..."
                className="w-full mt-1"
                styles={customStyles}
                isDisabled={!isUnionEditable()}
                isClearable
              />
            ) : (
              <p className="mt-1 text-sm text-gray-200">
                {workOrder.union || "Non-Union"}
              </p>
            )}
          </div>
        )}

        {/* Scope of Work */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400">
            Scope of Work
          </label>
          {editing ? (
            <textarea
              value={workOrder.scopeOfWork || ""}
              onChange={handleScopeChange}
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              rows="4"
            />
          ) : (
            <p className="mt-1 text-sm text-gray-200">
              {workOrder.scopeOfWork || "N/A"}
            </p>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400">
            Terms & Conditions
          </label>
          {editing ? (
            <textarea
              value={workOrder.termsAndConditions || ""}
              onChange={handleTermsChange}
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              rows="4"
            />
          ) : (
            <p className="mt-1 text-sm text-gray-200">
              {workOrder.termsAndConditions || "N/A"}
            </p>
          )}
        </div>

        {/* Cost Code */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400">
            Cost Code
          </label>
          {editing ? (
            <Select
              options={costCodeOptions}
              value={costCodeOptions.find(
                (opt) => opt.value === workOrder.costCode,
              )}
              onChange={handleCostCodeChange}
              placeholder="Select a cost code..."
              className="w-full mt-1"
              styles={customStyles}
            />
          ) : (
            <p className="mt-1 text-sm text-gray-200">
              {costCodes.find((cc) => cc._id === workOrder.costCode)
                ? `${
                    costCodes.find((cc) => cc._id === workOrder.costCode).code
                  } - ${
                    costCodes.find((cc) => cc._id === workOrder.costCode)
                      .description
                  }`
                : "N/A"}
            </p>
          )}
        </div>

        {/* Assigned To - Only for Subcontractor Type */}
        {workOrder.type === "subcontractor" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400">
              Assigned To
            </label>
            {editing ? (
              <Select
                options={subcontractorOptions}
                value={subcontractorOptions.find(
                  (opt) => opt.value === workOrder.assignedTo,
                )}
                onChange={handleAssigneeChange}
                placeholder="Select a subcontractor..."
                className="w-full mt-1"
                styles={customStyles}
              />
            ) : (
              <p className="mt-1 text-sm text-gray-200">
                {users.find((u) => u._id === workOrder.assignedTo)
                  ? `${
                      users.find((u) => u._id === workOrder.assignedTo)
                        .displayName
                    } (${
                      users.find((u) => u._id === workOrder.assignedTo).payType
                    })`
                  : "Unassigned"}
              </p>
            )}
          </div>
        )}

        {/* Add Labor Items */}
        {editing && showAddDropdown && (
          <div className="mb-4">
            <Select
              options={availableItemOptions}
              isMulti
              onChange={handleItemSelect}
              placeholder="Select labor items to add..."
              className="w-full"
              styles={customStyles}
              value={availableItemOptions.filter((opt) =>
                pendingItems.some((item) => item.budgetItem === opt.value),
              )}
            />
            {pendingItems.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-400">
                  Pending Items to Add
                </h4>
                <ul className="mt-2 space-y-2 text-sm text-gray-200">
                  {pendingItems.map((item, index) => (
                    <li key={item.budgetItem}>
                      {item.title} (Qty: {item.qtyEstimated}, Unit Cost: $
                      {item.unitCost.toFixed(2)}, UOM: {item.uom || "N/A"})
                    </li>
                  ))}
                </ul>
                <button
                  onClick={confirmAddItems}
                  className="mt-2 px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Confirm Add Items
                </button>
              </div>
            )}
          </div>
        )}

        {workOrder.type === "hourly" && (
          <div className="mb-6 p-6 bg-gray-800 rounded-lg border border-gray-600">
            <h3 className="text-lg font-semibold text-white mb-4">
              Timesheet Summary
            </h3>
            {timesheetSummary ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {timesheetSummary.totalHours}h
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Total Hours Charged
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    $
                    {timesheetSummary.totalCost.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Total Cost</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-300">
                    {timesheetSummary.timesheetCount}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Timesheets</div>
                </div>
                <div className="text-center md:col-span-2">
                  <div className="text-sm text-gray-400">
                    Subtotal: $
                    {timesheetSummary.totalSubtotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    {timesheetSummary.totalBurdenCost > 0 && (
                      <span>
                        {" "}
                        | Burden: $
                        {timesheetSummary.totalBurdenCost.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    )}
                    {timesheetSummary.totalWsibCost > 0 && (
                      <span>
                        {" "}
                        | WSIB: $
                        {timesheetSummary.totalWsibCost.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No approved timesheets found for this work order.
              </div>
            )}
          </div>
        )}
        {/* Budget Progress - Hourly */}
        {workOrder.type === "hourly" &&
          timesheetSummary &&
          (() => {
            const estimatedTotal = workOrder.lineItems.reduce(
              (sum, item) =>
                sum + (item.unitCost || 0) * (item.qtyEstimated || 0),
              0,
            );
            const actualTotal = timesheetSummary.totalCost;
            const pct =
              estimatedTotal > 0
                ? Math.min((actualTotal / estimatedTotal) * 100, 100)
                : 0;
            const over = estimatedTotal > 0 && actualTotal > estimatedTotal;
            const overPct =
              estimatedTotal > 0
                ? ((actualTotal / estimatedTotal) * 100).toFixed(1)
                : 0;
            return (
              <div className="mb-6 p-6 bg-gray-800 rounded-lg border border-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-white">
                    Budget Progress
                  </h3>
                  <span
                    className={`text-sm font-bold ${over ? "text-red-400" : "text-orange-400"}`}
                  >
                    {overPct}% used
                  </span>
                </div>
                <div className="w-full bg-gray-600 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 ${over ? "bg-red-500" : pct > 80 ? "bg-yellow-500" : "bg-orange-400"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>
                    Actual:{" "}
                    <span
                      className={`font-semibold ${over ? "text-red-400" : "text-white"}`}
                    >
                      $
                      {actualTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                  <span>
                    {over ? (
                      <span className="text-red-400 font-semibold">
                        Over by $
                        {(actualTotal - estimatedTotal).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    ) : (
                      <span>
                        Remaining:{" "}
                        <span className="font-semibold text-white">
                          $
                          {(estimatedTotal - actualTotal).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                      </span>
                    )}
                  </span>
                  <span>
                    Budget:{" "}
                    <span className="font-semibold text-white">
                      $
                      {estimatedTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                </div>
              </div>
            );
          })()}

        {workOrder.type === "piecework" && (
          <div className="mb-6 p-6 bg-gray-800 rounded-lg border border-gray-600">
            <h3 className="text-lg font-semibold text-white mb-4">
              Pieceworker Log Summary
            </h3>
            {pieceworkerSummary ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {pieceworkerSummary.totalQuantity}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Total Quantity Charged
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    $
                    {pieceworkerSummary.totalCost.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Total Cost</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-300">
                    {pieceworkerSummary.logCount}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Approved Logs
                  </div>
                </div>
                <div className="text-center md:col-span-2">
                  <div className="text-sm text-gray-400">
                    Subtotal: $
                    {pieceworkerSummary.totalSubtotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    {pieceworkerSummary.totalBurdenCost > 0 && (
                      <span>
                        {" "}
                        | Burden: $
                        {pieceworkerSummary.totalBurdenCost.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    )}
                    {pieceworkerSummary.totalWsibCost > 0 && (
                      <span>
                        {" "}
                        | WSIB: $
                        {pieceworkerSummary.totalWsibCost.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No approved pieceworker logs found for this work order.
              </div>
            )}
          </div>
        )}

        {workOrder.type === "piecework" &&
          pieceworkerSummary &&
          (() => {
            const estimatedTotal = workOrder.lineItems.reduce(
              (sum, item) =>
                sum + (item.unitCost || 0) * (item.qtyEstimated || 0),
              0,
            );
            const actualTotal = pieceworkerSummary.totalCost;
            const pct =
              estimatedTotal > 0
                ? Math.min((actualTotal / estimatedTotal) * 100, 100)
                : 0;
            const over = estimatedTotal > 0 && actualTotal > estimatedTotal;
            const overPct =
              estimatedTotal > 0
                ? ((actualTotal / estimatedTotal) * 100).toFixed(1)
                : 0;
            return (
              <div className="mb-6 p-6 bg-gray-800 rounded-lg border border-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-white">
                    Budget Progress
                  </h3>
                  <span
                    className={`text-sm font-bold ${over ? "text-red-400" : "text-orange-400"}`}
                  >
                    {overPct}% used
                  </span>
                </div>
                <div className="w-full bg-gray-600 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 ${over ? "bg-red-500" : pct > 80 ? "bg-yellow-500" : "bg-orange-400"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>
                    Actual:{" "}
                    <span
                      className={`font-semibold ${over ? "text-red-400" : "text-white"}`}
                    >
                      $
                      {actualTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                  <span>
                    {over ? (
                      <span className="text-red-400 font-semibold">
                        Over by $
                        {(actualTotal - estimatedTotal).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    ) : (
                      <span>
                        Remaining:{" "}
                        <span className="font-semibold text-white">
                          $
                          {(estimatedTotal - actualTotal).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                      </span>
                    )}
                  </span>
                  <span>
                    Budget:{" "}
                    <span className="font-semibold text-white">
                      $
                      {estimatedTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                </div>
              </div>
            );
          })()}

        {/* Line Items Table */}
        <div className="overflow-hidden rounded-lg shadow-xl">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                  Title
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Quantity Estimated
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Unit Cost
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  UOM
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Labor Hours
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Bid Area
                </th>
                {workOrder.type === "piecework" && (
                  <>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Installed
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Pending
                    </th>
                  </>
                )}

                {editing && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                    Actions
                  </th>
                )}
                {!editing &&
                  workOrder.type !== "piecework" &&
                  workOrder.type !== "hourly" && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg"></th>
                  )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {workOrder.lineItems.map((item, index) => {
                const canEdit = canEditOrRemoveItem(item);
                const latestProgress =
                  item.progressUpdates.length > 0
                    ? item.progressUpdates[item.progressUpdates.length - 1]
                    : null;
                return (
                  <tr
                    key={item._id || index}
                    className={`${
                      highlightedItems.has(item.budgetItem)
                        ? "bg-orange-100 transition-colors duration-1000"
                        : "hover:bg-gray-600"
                    } transition-colors duration-200`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {item.title}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {item.qtyEstimated}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      ${item.unitCost.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      ${(item.unitCost * item.qtyEstimated).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {item.uom || "N/A"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {item.laborHoursEstimated || 0}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {item.bidArea || ""}
                    </td>
                    {workOrder.type === "piecework" && (
                      <>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {item.qtyInstalled || 0}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {item.qtyPending || 0}
                        </td>
                      </>
                    )}

                    {editing && (
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        <button
                          onClick={() => handleRemoveItem(index, item)}
                          className="px-2 py-1 text-sm font-medium text-white bg-red-400 rounded-md hover:bg-red-500 transition-colors duration-200"
                          disabled={!canEdit}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {workOrder.lineItems.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      workOrder.type === "piecework" ? 10 : editing ? 9 : 8
                    }
                    className="px-4 py-4 text-center text-sm text-gray-400"
                  >
                    No line items found.
                  </td>
                </tr>
              )}

              {workOrder.lineItems.length > 0 && (
                <tr className="bg-gray-800 font-semibold">
                  <td className="px-4 py-3 text-sm text-white">Total</td>
                  <td className="px-4 py-3 text-sm text-white">
                    {workOrder.lineItems.reduce(
                      (sum, item) => sum + (item.qtyEstimated || 0),
                      0,
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-orange-400">
                    $
                    {workOrder.lineItems
                      .reduce(
                        (sum, item) =>
                          sum + (item.unitCost || 0) * (item.qtyEstimated || 0),
                        0,
                      )
                      .toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-white">
                    {workOrder.lineItems
                      .reduce(
                        (sum, item) => sum + (item.laborHoursEstimated || 0),
                        0,
                      )
                      .toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  {workOrder.type === "piecework" && (
                    <>
                      <td className="px-4 py-3 text-sm text-white">
                        {workOrder.lineItems.reduce(
                          (sum, item) => sum + (item.qtyInstalled || 0),
                          0,
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {workOrder.lineItems.reduce(
                          (sum, item) => sum + (item.qtyPending || 0),
                          0,
                        )}
                      </td>
                    </>
                  )}

                  {editing && <td className="px-4 py-3" />}
                  {!editing &&
                    workOrder.type !== "piecework" &&
                    workOrder.type !== "hourly" && <td className="px-4 py-3" />}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Cancel"
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={dialog.onConfirm}
        onCancel={dialog.onCancel}
      />
    </div>
  );
}
